import type { FC, KeyboardEvent, ReactNode, Ref } from "react"
import { forwardRef } from "react"
import {
  Box,
  Checkbox,
  FormControlLabel,
  type InputLabelProps,
  TextField,
} from "@mui/material"
import InputAdornment from "@mui/material/InputAdornment"
import MenuItem from "@mui/material/MenuItem"
import { useController, useFormContext } from "react-hook-form"
import { get } from "lodash/fp"
import { useTranslation } from "react-i18next"

import type { InputRules, SelectOption } from "types/common.types"
import { getLocaleSeparators, isValidLocaleNumber } from "helpers/utils/localeNumber"

interface ControlledInputProps {
  type?: string
  id?: string
  name: string
  label?: string
  placeholder?: string
  required?: boolean
  styles?: object
  variant?: "standard" | "filled" | "outlined" | undefined
  selectOptions?: SelectOption[] | null
  defaultChecked?: boolean
  rules?: InputRules
  endAdornment?: ReactNode | string
  defaultValue?: string | number | any[] | boolean
  size?: "small" | "medium" | undefined
  disabled?: boolean
  inputProps?: any
  inputLabelProps?: Partial<InputLabelProps>
  "aria-label"?: React.ComponentProps<"input">["aria-label"]
  autoFocus?: boolean
}

const ControlledInput: FC<ControlledInputProps> = forwardRef(
  function ControlledInputInner(
    {
      type = "text",
      name,
      label = "",
      placeholder,
      selectOptions,
      defaultValue,
      variant = "standard",
      styles,
      defaultChecked,
      rules,
      endAdornment,
      size,
      disabled,
      inputLabelProps,
      "aria-label": ariaLabel = "input",
      ...props
    }: ControlledInputProps,
    ref: Ref<HTMLInputElement> | null,
  ) {
    const { control } = useFormContext()
    const { t, i18n } = useTranslation()
    const isNumeric = type === "number"
    const locale = i18n.resolvedLanguage || "en"

    const rulesWithExtras = isNumeric
      ? {
          ...rules,
          validate: {
            ...(typeof rules?.validate === "object" ? rules.validate : {}),
            ...(typeof rules?.validate === "function" ? { _custom: rules.validate } : {}),
            numericFormat: (v: string) =>
              !v ||
              isValidLocaleNumber(v, locale) ||
              t("generic.FIELD_INVALID_NUMBER", { defaultValue: "Invalid number" }),
          },
        }
      : rules

    const {
      field: { name: fieldName, onBlur, onChange, value, ref: fieldRef },
      formState: { errors },
    } = useController<Record<string, any>>({
      rules: rulesWithExtras,
      name,
      control,
      ...((typeof defaultValue === "string" || typeof defaultValue === "number") && {
        defaultValue: `${defaultValue}`,
      }),
    })

    const inputErrorObj = get(name, errors)
    const { decimal, thousands } = getLocaleSeparators(locale)

    const handleNumericKeyDown = isNumeric
      ? (e: KeyboardEvent<HTMLInputElement>) => {
          const allowedControlKeys = [
            "Backspace",
            "Delete",
            "Tab",
            "Escape",
            "Enter",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Home",
            "End",
          ]
          if (allowedControlKeys.includes(e.key)) return
          if (e.ctrlKey || e.metaKey) return
          if (e.key === "-" && (e.target as HTMLInputElement).selectionStart === 0) return
          // Scientific notation: allow 'e' and 'E' (once), and '+'/'-' after e
          if (
            (e.key === "e" || e.key === "E") &&
            !(e.target as HTMLInputElement).value.toLowerCase().includes("e")
          )
            return
          if (
            (e.key === "+" || e.key === "-") &&
            /[eE]$/.test(
              (e.target as HTMLInputElement).value.slice(
                0,
                (e.target as HTMLInputElement).selectionStart ?? undefined,
              ),
            )
          )
            return
          // Thousands separator: allow multiple
          if (e.key === thousands) return
          // Decimal separator: allow only one
          if (
            e.key === decimal &&
            !(e.target as HTMLInputElement).value.includes(decimal)
          )
            return
          if (!/^\d$/.test(e.key)) e.preventDefault()
        }
      : undefined

    return (
      <Box width={"100%"}>
        {type === "checkbox" ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={value !== undefined ? !!value : defaultChecked}
                onChange={onChange}
                disabled={disabled}
              />
            }
            label={label}
            sx={{
              "& .MuiFormControlLabel-label.Mui-disabled": {
                color: (theme) => theme.palette.text.primary,
              },
            }}
          />
        ) : (
          <TextField
            {...props}
            {...(typeof defaultValue !== "string" &&
              defaultValue !== undefined &&
              defaultValue !== null &&
              typeof defaultValue !== "number" && { defaultValue })}
            value={value}
            name={fieldName}
            size={size}
            type={isNumeric ? "text" : type}
            select={type === "select"}
            onBlur={onBlur}
            onChange={onChange}
            onKeyDown={handleNumericKeyDown}
            label={label}
            placeholder={placeholder}
            inputRef={ref || fieldRef}
            fullWidth
            error={!!inputErrorObj}
            helperText={(inputErrorObj?.message as string | undefined) || ""}
            required={!!rules?.required}
            variant={variant}
            disabled={disabled}
            InputProps={{
              endAdornment: endAdornment && (
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              ),
            }}
            inputProps={{
              "aria-label": ariaLabel,
              role: "textbox",
              ...(isNumeric && { inputMode: "decimal" as const }),
            }}
            sx={{
              "& .MuiFormHelperText-root": {
                textAlign: "right",
                marginRight: "0px",
              },
              ...(variant === "standard"
                ? {
                    "& .MuiInputBase-root .MuiFormLabel-root": {
                      fontSize: "1rem",
                    },

                    marginBottom: 0,
                    ...styles,
                  }
                : { ...styles }),
            }}
            InputLabelProps={inputLabelProps}
          >
            {type === "select" &&
              selectOptions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label, { defaultValue: option.label })}
                </MenuItem>
              ))}
          </TextField>
        )}
      </Box>
    )
  },
)

export default ControlledInput
